import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { AnimatePresence, PanInfo, useMotionValue, animate } from "framer-motion";
import { useLocation, useSearchParams } from "react-router";
import { useSidebarContext } from "@/hooks/use-sidebar";
import { useWorkspace } from "@/hooks/use-workspace";
import { FaPlus } from "react-icons/fa";
import {
  useFocusedPanelType,
  useSplitViewActive,
  useHasMultipleTabs,
} from "@/hooks/use-tabs-store";

// Import sidebar sections
import SidebarHeader from "./sidebar-header";
import AppFavoritesSection from "./app-favorites-section";
import PinnedSection from "./pinned-section";
import DashboardGroupsSection from "./dashboard-groups-section";
import TrackerGroupsSection from "./tracker-groups-section";
import EnrichmentGroupsSection from "./enrichment-groups-section";
import AlertGroupsSection from "./alert-groups-section";
import ConversationsSection from "./conversations-section";
import BranchesSection from "./branches-section";
import NewDashboardButton from "./new-dashboard-button";
import NewTrackerButton from "./new-tracker-button";
import NewEnrichmentButton from "./new-enrichment-button";
import NewAlertButton from "./new-alert-button";
import NewConversationButton from "./new-conversation-button";
import WorkspaceSlider from "./workspace-slider";
import DashboardContextMenu from "./dashboard-context-menu";
import WidgetsSection from "./widgets-section";
import InvestigationGroupsSection from "./investigation-groups-section";
import MultiSelectActionBar from "./multi-select-action-bar";
import MultiSelectContextMenu from "./multi-select-context-menu";
// Account/Settings sections
import WorkspacesSection from "./workspaces-section";
import TokensSection from "./tokens-section";
import MembersSection from "./members-section";
import ChannelsSection from "./channels-section";
import ReportsSection from "./reports-section";
import { MotionAside, MotionDiv } from "./motion-types";
// TEMPORARY: ThemeModeToggle removed - dark mode is disabled
// import ThemeModeToggle from "./theme-mode-toggle";
import { useWorkspaceTheme } from "@/hooks/use-workspace-theme";
import { useGestureScroll } from "@/hooks/use-gesture-scroll";
import { Z_INDEX } from "@/utils/z-index";
import { useMultiSelect } from "@/hooks/use-multi-select";
import { useMultiSelectOperations } from "@/hooks/use-multi-select/operations";

/** Page types that determine which sidebar content to show */
type SidebarPageType = 
  | "chat" 
  | "alerts" 
  | "enrichments" 
  | "trackers" 
  | "dashboards" 
  | "widgets" 
  | "branches" 
  | "investigations" 
  | "workspaces"
  | "tokens"
  | "members"
  | "channels"
  | "reports"
  | "organization"
  | "settings";

// Sidebar dimensions
const COLLAPSED_WIDTH = 56;

// =============================================================================
// Section Configuration - Maps pageType to its content renderer
// =============================================================================

interface SectionConfig {
  /** Component to render for this section (receives onMultiSelectContextMenu) */
  Component?: React.FC<{ onMultiSelectContextMenu?: (e: React.MouseEvent) => void }>;
  /** Component for the "New" button at the bottom */
  NewButtonComponent?: React.FC;
  /** Custom render function (for dashboards which have special logic) */
  customRender?: (
    isExpanded: boolean,
    onMultiSelectContextMenu: (e: React.MouseEvent) => void,
    dashboards?: unknown[]
  ) => React.ReactNode;
}

// Swipe configuration for workspace navigation
const SWIPE_THRESHOLD = 50; // Minimum distance to trigger a swipe
const SWIPE_VELOCITY_THRESHOLD = 300; // Minimum velocity to trigger a swipe
const WHEEL_DEBOUNCE_MS = 200; // Debounce time for wheel events

/**
 * Arc-style sidebar component with collapsible functionality.
 * Features:
 * - App favorites grid
 * - Pinned dashboards
 * - Dashboard groups
 * - Workspace switcher with page-like swipe navigation
 * - Context menu (right-click) on dashboards
 * - Keyboard shortcuts
 */
const ArcSidebar: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    isOpen,
    sidebarWidth,
    setSidebarWidth,
    isLocked,
    toggleLock,
    currentWorkspaceIndex,
    setCurrentWorkspaceIndex,
    contextMenu,
    closeContextMenu,
  } = useSidebarContext();

  const { workspaces, selectWorkspace } = useWorkspace();
  const { mode } = useWorkspaceTheme();

  // Multi-select hook
  const { hasSelection, clearSelection, itemType: selectedItemType } = useMultiSelect();

  // Multi-select operations hook
  const multiSelectOps = useMultiSelectOperations();

  // Multi-select context menu state
  const [multiSelectContextMenu, setMultiSelectContextMenu] = useState<{
    position: { x: number; y: number };
  } | null>(null);

  // Split view state - used to determine sidebar content based on focused panel
  // Use optimized hook that only returns the panel type (not all panels)
  const focusedPanelType = useFocusedPanelType();
  const isSplitViewActive = useSplitViewActive();
  const hasMultipleTabs = useHasMultipleTabs();

  /**
   * Determine the effective page type for sidebar content.
   * When split view is active OR when we have multiple tabs, use the focused panel's type.
   * Otherwise, fall back to URL pathname detection.
   */
  const pageType: SidebarPageType = useMemo(() => {
    // When split view is active OR we have multiple tabs, use the focused panel's type
    // This ensures sidebar updates when switching between tabs (even with 1 panel each)
    if ((isSplitViewActive || hasMultipleTabs) && focusedPanelType) {
      switch (focusedPanelType) {
        case "chat":
          return "chat";
        case "tracker":
        case "trackers":
          return "trackers";
        case "enrichment":
        case "enrichments":
          return "enrichments";
        case "alert":
        case "alerts":
          return "alerts";
        case "widgets":
          return "widgets";
        case "branches":
          return "branches";
        case "investigation":
        case "investigations":
          return "investigations";
        case "workspaces":
          return "workspaces";
        case "tokens":
          return "tokens";
        case "members":
          return "members";
        case "channels":
          return "channels";
        case "reports":
          return "reports";
        case "organization":
          return "organization";
        case "settings":
          return "settings";
        default:
          return "dashboards";
      }
    }

    // Fallback: use URL pathname (normal navigation mode)
    if (location.pathname.startsWith("/chat")) {
      return "chat";
    }
    if (location.pathname.startsWith("/widgets")) {
      return "widgets";
    }
    if (location.pathname.startsWith("/branches")) {
      return "branches";
    }
    if (location.pathname.startsWith("/investigations")) {
      return "investigations";
    }
    if (location.pathname.startsWith("/enrichments")) {
      return "enrichments";
    }
    if (location.pathname.startsWith("/trackers")) {
      return "trackers";
    }
    if (location.pathname.startsWith("/alerts")) {
      return "alerts";
    }
    // Account routes with dedicated sidebar sections
    if (location.pathname.startsWith("/workspaces")) {
      return "workspaces";
    }
    if (location.pathname.startsWith("/tokens")) {
      return "tokens";
    }
    if (location.pathname.startsWith("/members")) {
      return "members";
    }
    if (location.pathname.startsWith("/channels")) {
      return "channels";
    }
    if (location.pathname.startsWith("/reports")) {
      return "reports";
    }
    // Account routes without dedicated sidebar sections
    if (location.pathname.startsWith("/organization")) {
      return "organization";
    }
    if (location.pathname.startsWith("/settings") || location.pathname.startsWith("/plan")) {
      return "settings";
    }
    return "dashboards";
  }, [isSplitViewActive, hasMultipleTabs, focusedPanelType, location.pathname]);

  const [isHovered, setIsHovered] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<number>(0); // -1 = left, 1 = right, 0 = none
  
  // Ref to track previous workspace index for animation direction
  const prevIndexRef = useRef<number>(currentWorkspaceIndex);
  
  // Ref for sidebar element (used by gesture scroll hook)
  const sidebarRef = useRef<HTMLElement>(null);
  
  // Motion value for drag position
  const dragX = useMotionValue(0);

  // Determine if sidebar should be expanded (locked, open, hovered, or context menu open)
  const isExpanded = isLocked || isOpen || isHovered || !!contextMenu;
  
  // Determine if sidebar is in overlay mode (expanded but not locked)
  const isOverlayMode = isExpanded && !isLocked;
  
  // Total pages = workspaces + 1 (for create new)
  const totalPages = workspaces.length + 1;

  // Track direction for animation
  useEffect(() => {
    if (currentWorkspaceIndex > prevIndexRef.current) {
      setSwipeDirection(1); // Going forward (left animation)
    } else if (currentWorkspaceIndex < prevIndexRef.current) {
      setSwipeDirection(-1); // Going backward (right animation)
    }
    prevIndexRef.current = currentWorkspaceIndex;
  }, [currentWorkspaceIndex]);

  // Handle workspace selection
  const handleWorkspaceSelect = useCallback(
    (index: number) => {
      setCurrentWorkspaceIndex(index);
      // Only select workspace if it exists (not on create page)
      if (workspaces[index]) {
        selectWorkspace(workspaces[index].id);
      }
    },
    [workspaces, setCurrentWorkspaceIndex, selectWorkspace]
  );

  // Go to previous workspace (wraps around from first to last)
  const goToPrevious = useCallback(() => {
    if (currentWorkspaceIndex > 0) {
      handleWorkspaceSelect(currentWorkspaceIndex - 1);
    } else {
      // Wrap around to the last page (create new workspace)
      handleWorkspaceSelect(totalPages - 1);
    }
  }, [currentWorkspaceIndex, totalPages, handleWorkspaceSelect]);

  // Go to next workspace (wraps around from last to first)
  const goToNext = useCallback(() => {
    if (currentWorkspaceIndex < totalPages - 1) {
      handleWorkspaceSelect(currentWorkspaceIndex + 1);
    } else {
      // Wrap around to the first workspace
      handleWorkspaceSelect(0);
    }
  }, [currentWorkspaceIndex, totalPages, handleWorkspaceSelect]);

  // Use gesture scroll hook for horizontal workspace navigation
  useGestureScroll(sidebarRef, {
    direction: "horizontal",
    totalPages,
    currentIndex: currentWorkspaceIndex,
    onPrevious: goToPrevious,
    onNext: goToNext,
    disabled: workspaces.length === 0,
  });

  // Handle drag end - calculate which workspace to snap to (with wrap-around)
  const handleDragEnd = useCallback(
    (_event: any, info: PanInfo) => {
      if (workspaces.length === 0) return;
      
      const offset = info.offset.x;
      const velocity = info.velocity.x;

      // Determine direction based on velocity or offset
      let direction = 0;
      if (Math.abs(velocity) > SWIPE_VELOCITY_THRESHOLD) {
        // Swipe right (positive velocity) = go to previous (direction -1)
        // Swipe left (negative velocity) = go to next (direction +1)
        direction = velocity > 0 ? -1 : 1;
      } else if (Math.abs(offset) > SWIPE_THRESHOLD) {
        direction = offset > 0 ? -1 : 1;
      }

      // Calculate new index with wrap-around
      let newIndex = currentWorkspaceIndex + direction;
      if (newIndex < 0) {
        newIndex = totalPages - 1; // Wrap to last (create new workspace)
      } else if (newIndex >= totalPages) {
        newIndex = 0; // Wrap to first workspace
      }
      
      if (newIndex !== currentWorkspaceIndex) {
        handleWorkspaceSelect(newIndex);
      }
      
      // Reset drag position with spring animation
      animate(dragX, 0, { type: "spring", stiffness: 400, damping: 35 });
    },
    [currentWorkspaceIndex, totalPages, handleWorkspaceSelect, dragX, workspaces.length]
  );

  // Keyboard shortcuts for workspace switching (⌘1-9) and multi-select (Escape, ⌘A)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Workspace switching shortcuts (⌘1-9)
      if ((e.metaKey || e.ctrlKey) && e.key >= "1" && e.key <= "9") {
        const index = parseInt(e.key) - 1;
        if (index < workspaces.length) {
          e.preventDefault();
          handleWorkspaceSelect(index);
        }
      }

      // Multi-select: Escape to clear selection
      if (e.key === "Escape" && hasSelection) {
        e.preventDefault();
        clearSelection();
        setMultiSelectContextMenu(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [workspaces.length, handleWorkspaceSelect, hasSelection, clearSelection]);

  // Refs for resize cleanup
  const resizeCleanupRef = useRef<(() => void) | null>(null);

  // Cleanup resize handlers on unmount
  useEffect(() => {
    return () => {
      if (resizeCleanupRef.current) {
        resizeCleanupRef.current();
      }
    };
  }, []);

  // Handle resize drag
  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);

      const startX = e.clientX;
      const startWidth = sidebarWidth;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientX - startX;
        const newWidth = Math.max(200, Math.min(400, startWidth + delta));
        setSidebarWidth(newWidth);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        resizeCleanupRef.current = null;
      };

      // Store cleanup function for unmount
      resizeCleanupRef.current = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [sidebarWidth, setSidebarWidth]
  );

  // Check if we're on the "create new workspace" page
  const isOnCreatePage = currentWorkspaceIndex === workspaces.length;

  // Mode-aware colors for create page (using semantic tokens)
  const textMutedColor = mode === "night" ? "text-white/60" : "text-content-tertiary";
  const textColor = mode === "night" ? "text-white" : "text-content-primary";
  const borderColor = mode === "night" ? "border-white/10" : "border-black/10";
  const bgHoverColor = mode === "night" ? "hover:bg-white/10" : "hover:bg-black/5";

  // Animation variants for page transitions
  const pageVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? "100%" : "-100%",
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction > 0 ? "-100%" : "100%",
      opacity: 0,
    }),
  };

  const pageTransition = {
    type: "spring" as const,
    stiffness: 400,
    damping: 35,
    mass: 0.8,
  };

  // Handler to open multi-select context menu
  const handleMultiSelectContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setMultiSelectContextMenu({
      position: { x: e.clientX, y: e.clientY },
    });
  }, []);

  // Close multi-select context menu
  const closeMultiSelectContextMenu = useCallback(() => {
    setMultiSelectContextMenu(null);
  }, []);

  return (
    <>
      {/* Sidebar Container - Uses CSS variables for real-time theme updates */}
      <MotionAside
        ref={sidebarRef}
        initial={false}
        animate={{
          width: isExpanded ? sidebarWidth : COLLAPSED_WIDTH,
        }}
        transition={{
          type: "tween",
          duration: 0.2,
          ease: "easeOut",
        }}
        onMouseEnter={() => !isOpen && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="arc-sidebar fixed left-0 top-0 h-screen flex flex-col shadow-2xl rounded-r-lg overflow-hidden"
        style={{
          zIndex: isOverlayMode ? Z_INDEX.SIDEBAR_OVERLAY : Z_INDEX.SIDEBAR,
          // Prevent browser back/forward navigation on horizontal swipe
          overscrollBehaviorX: "none",
        }}
      >
        {/* Fixed Header - stays in place during swipe */}
        <SidebarHeader
          isCollapsed={!isExpanded}
          isLocked={isLocked}
          onToggleLock={toggleLock}
        />

        {/* App Favorites - Fixed, stays in place during swipe */}
        <AppFavoritesSection isCollapsed={!isExpanded} />

        {/* Pages Container - swipeable area */}
        <MotionDiv
          drag={isExpanded && workspaces.length > 0 ? "x" : false}
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.2}
          onDragEnd={handleDragEnd as any}
          style={{ x: dragX } as any}
          className="flex-1 relative overflow-hidden"
        >
          {/* Animated page content */}
          <AnimatePresence mode="wait" custom={swipeDirection}>
            {isOnCreatePage ? (
              // Create New Workspace Page
              <MotionDiv
                key="create-workspace-page"
                custom={swipeDirection}
                variants={pageVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={pageTransition}
                className="absolute inset-0 flex flex-col items-center justify-center overflow-hidden"
              >
                {isExpanded && (
                  <div className="flex flex-col items-center justify-center gap-4 p-6">
                    <div className={`w-16 h-16 rounded-2xl border-2 border-dashed ${borderColor} flex items-center justify-center ${bgHoverColor} transition-colors cursor-pointer`}>
                      <FaPlus className={textMutedColor} size={24} />
                    </div>
                    <div className="text-center">
                      <p className={`${textColor} font-medium text-sm`}>New Workspace</p>
                      <p className={`${textMutedColor} text-xs mt-1`}>Swipe back or click below</p>
                    </div>
                  </div>
                )}
              </MotionDiv>
            ) : (
              // Workspace Content Page
              <MotionDiv
                key="workspace-content"
                custom={swipeDirection}
                variants={pageVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={pageTransition}
                className="absolute inset-0 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent"
              >
                {pageType === "chat" ? (
                  // Chat page - show conversations
                  <>
                    {isExpanded && <ConversationsSection onMultiSelectContextMenu={handleMultiSelectContextMenu} />}
                  </>
                ) : pageType === "alerts" ? (
                  // Alert page - show alerts grouped by preference
                  <>
                    {isExpanded && <AlertGroupsSection onMultiSelectContextMenu={handleMultiSelectContextMenu} />}
                  </>
                ) : pageType === "enrichments" ? (
                  // Enrichments tab - show enrichments grouped by preference
                  <>
                    {isExpanded && <EnrichmentGroupsSection onMultiSelectContextMenu={handleMultiSelectContextMenu} />}
                  </>
                ) : pageType === "trackers" ? (
                  // Trackers page - show trackers grouped by preference
                  <>
                    {isExpanded && <TrackerGroupsSection onMultiSelectContextMenu={handleMultiSelectContextMenu} />}
                  </>
                ) : pageType === "widgets" ? (
                  // Widgets gallery - show widgets grouped by type/dashboard
                  <>
                    {isExpanded && <WidgetsSection onMultiSelectContextMenu={handleMultiSelectContextMenu} />}
                  </>
                ) : pageType === "branches" ? (
                  // Branches page - show branches list
                  <>
                    {isExpanded && <BranchesSection />}
                  </>
                ) : pageType === "investigations" ? (
                  // Investigations page - show investigations grouped by preference
                  <>
                    {isExpanded && <InvestigationGroupsSection onMultiSelectContextMenu={handleMultiSelectContextMenu} />}
                  </>
                ) : pageType === "workspaces" ? (
                  // Workspaces page - show workspaces list
                  <>
                    {isExpanded && <WorkspacesSection />}
                  </>
                ) : pageType === "tokens" ? (
                  // Tokens page - show API tokens list
                  <>
                    {isExpanded && <TokensSection />}
                  </>
                ) : pageType === "members" ? (
                  // Members page - show members list
                  <>
                    {isExpanded && <MembersSection />}
                  </>
                ) : pageType === "channels" ? (
                  // Channels page - show notification channels list
                  <>
                    {isExpanded && <ChannelsSection />}
                  </>
                ) : pageType === "reports" ? (
                  // Reports page - show PDF reports list
                  <>
                    {isExpanded && <ReportsSection onMultiSelectContextMenu={handleMultiSelectContextMenu} />}
                  </>
                ) : pageType === "organization" || pageType === "settings" ? (
                  // Organization/Settings pages - no sidebar section needed
                  <></>
                ) : (
                  // Dashboards (default)
                  <>
                    {/* Pinned Dashboards */}
                    {isExpanded && <PinnedSection />}

                    {/* Dashboard Groups */}
                    {isExpanded && <DashboardGroupsSection onMultiSelectContextMenu={handleMultiSelectContextMenu} />}
                  </>
                )}
              </MotionDiv>
            )}
          </AnimatePresence>
        </MotionDiv>

        {/* New Dashboard/Conversation/Tracker/Enrichment/Alert Button - Fixed, stays in place during swipe */}
        {isExpanded && !isOnCreatePage && 
         pageType !== "widgets" && 
         pageType !== "branches" && 
         pageType !== "investigations" &&
         pageType !== "workspaces" &&
         pageType !== "tokens" &&
         pageType !== "members" &&
         pageType !== "channels" &&
         pageType !== "reports" &&
         pageType !== "organization" &&
         pageType !== "settings" && (
          pageType === "chat" ? (
            <NewConversationButton />
          ) : pageType === "alerts" ? (
            <NewAlertButton />
          ) : pageType === "enrichments" ? (
            <NewEnrichmentButton />
          ) : pageType === "trackers" ? (
            <NewTrackerButton />
          ) : (
            <NewDashboardButton />
          )
        )}

        {/* Bottom Section - Fixed, stays in place during swipe */}
        <div className="mt-auto">
          {/* Workspace Slider */}
          {isExpanded && workspaces.length > 0 && (
            <WorkspaceSlider
              workspaces={workspaces}
              currentIndex={currentWorkspaceIndex}
              onSelect={handleWorkspaceSelect}
              isPending={false}
            />
          )}

          {/* TEMPORARY: Theme Mode Toggle removed - dark mode is disabled */}
          {/* <ThemeModeToggle isCollapsed={!isExpanded} /> */}
        </div>

        {/* Resize Handle */}
        {isExpanded && (
          <div
            onMouseDown={handleResizeStart}
            className={`absolute right-0 top-0 w-1 h-full cursor-ew-resize hover:bg-blue-500/50 transition-colors ${
              isResizing ? "bg-blue-500/50" : ""
            }`}
          />
        )}

        {/* Multi-select Action Bar */}
        {isExpanded && hasSelection && (
          <MultiSelectActionBar
            // Dashboard actions
            onDeleteDashboards={selectedItemType === "dashboard" ? multiSelectOps.deleteDashboards : undefined}
            onDuplicateDashboards={selectedItemType === "dashboard" ? multiSelectOps.duplicateDashboards : undefined}
            onPinDashboards={selectedItemType === "dashboard" ? multiSelectOps.pinDashboards : undefined}
            onChangeDashboardsColor={selectedItemType === "dashboard" ? multiSelectOps.changeDashboardsColor : undefined}

            // Tracker actions
            onDeleteTrackers={selectedItemType === "tracker" ? multiSelectOps.deleteTrackers : undefined}
            onActivateTrackers={selectedItemType === "tracker" ? multiSelectOps.activateTrackers : undefined}
            onDeactivateTrackers={selectedItemType === "tracker" ? multiSelectOps.deactivateTrackers : undefined}
            onRunTrackers={selectedItemType === "tracker" ? multiSelectOps.runTrackers : undefined}

            // Enrichment actions
            onDeleteEnrichments={selectedItemType === "enrichment" ? multiSelectOps.deleteEnrichments : undefined}
            onActivateEnrichments={selectedItemType === "enrichment" ? multiSelectOps.activateEnrichments : undefined}
            onDeactivateEnrichments={selectedItemType === "enrichment" ? multiSelectOps.deactivateEnrichments : undefined}
            onRunEnrichments={selectedItemType === "enrichment" ? multiSelectOps.runEnrichments : undefined}

            // Alert actions
            onDeleteAlerts={selectedItemType === "alert" ? multiSelectOps.deleteAlerts : undefined}
            onActivateAlerts={selectedItemType === "alert" ? multiSelectOps.activateAlerts : undefined}
            onDeactivateAlerts={selectedItemType === "alert" ? multiSelectOps.deactivateAlerts : undefined}
            onRunAlerts={selectedItemType === "alert" ? multiSelectOps.runAlerts : undefined}

            // Widget actions
            onDeleteWidgets={selectedItemType === "widget" ? multiSelectOps.deleteWidgets : undefined}

            // Investigation actions
            onDeleteInvestigations={selectedItemType === "investigation" ? multiSelectOps.deleteInvestigations : undefined}
          />
        )}
      </MotionAside>

      {/* Resize overlay to prevent text selection during resize */}
      {isResizing && <div className="fixed inset-0 z-40 cursor-ew-resize" />}

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu && (
          <DashboardContextMenu
            position={contextMenu.position}
            dashboard={contextMenu.dashboard}
            onClose={closeContextMenu}
          />
        )}
      </AnimatePresence>

      {/* Multi-select Context Menu */}
      <AnimatePresence>
        {multiSelectContextMenu && hasSelection && (
          <MultiSelectContextMenu
            position={multiSelectContextMenu.position}
            onClose={closeMultiSelectContextMenu}
            // Dashboard actions
            onDeleteDashboards={selectedItemType === "dashboard" ? multiSelectOps.deleteDashboards : undefined}
            onDuplicateDashboards={selectedItemType === "dashboard" ? multiSelectOps.duplicateDashboards : undefined}
            onPinDashboards={selectedItemType === "dashboard" ? multiSelectOps.pinDashboards : undefined}
            onChangeDashboardsColor={selectedItemType === "dashboard" ? multiSelectOps.changeDashboardsColor : undefined}

            // Tracker actions
            onDeleteTrackers={selectedItemType === "tracker" ? multiSelectOps.deleteTrackers : undefined}
            onActivateTrackers={selectedItemType === "tracker" ? multiSelectOps.activateTrackers : undefined}
            onDeactivateTrackers={selectedItemType === "tracker" ? multiSelectOps.deactivateTrackers : undefined}
            onRunTrackers={selectedItemType === "tracker" ? multiSelectOps.runTrackers : undefined}

            // Enrichment actions
            onDeleteEnrichments={selectedItemType === "enrichment" ? multiSelectOps.deleteEnrichments : undefined}
            onActivateEnrichments={selectedItemType === "enrichment" ? multiSelectOps.activateEnrichments : undefined}
            onDeactivateEnrichments={selectedItemType === "enrichment" ? multiSelectOps.deactivateEnrichments : undefined}
            onRunEnrichments={selectedItemType === "enrichment" ? multiSelectOps.runEnrichments : undefined}

            // Alert actions
            onDeleteAlerts={selectedItemType === "alert" ? multiSelectOps.deleteAlerts : undefined}
            onActivateAlerts={selectedItemType === "alert" ? multiSelectOps.activateAlerts : undefined}
            onDeactivateAlerts={selectedItemType === "alert" ? multiSelectOps.deactivateAlerts : undefined}
            onRunAlerts={selectedItemType === "alert" ? multiSelectOps.runAlerts : undefined}

            // Widget actions
            onDeleteWidgets={selectedItemType === "widget" ? multiSelectOps.deleteWidgets : undefined}

            // Investigation actions
            onDeleteInvestigations={selectedItemType === "investigation" ? multiSelectOps.deleteInvestigations : undefined}
          />
        )}
      </AnimatePresence>
    </>
  );
};

ArcSidebar.displayName = "ArcSidebar";

export default ArcSidebar;
